// SSO initiate + callback parsing for backend-driven OAuth (SSO Integration Guide)
import { API_CONFIG, API_ENDPOINTS, USER_ROLES } from '../helpers/constants';
import type { LoginResponse } from './authService';

export type SsoProvider = 'google' | 'microsoft';

/** Backend error shape for /initiate and callback redirects */
export interface SsoErrorBody {
  error_code?: string;
  message?: string;
  detail?: string;
}

export type SsoDomainStatus = 'known' | 'unknown' | 'skipped';

export interface SsoCallbackError {
  errorCode?: string;
  message: string;
  domainStatus?: SsoDomainStatus;
  email?: string;
  accessRequestToken?: string;
}

export function isSsoNoInviteError(errorCode?: string | null, message?: string | null): boolean {
  const normalizedCode = (errorCode || '').trim().toLowerCase();
  const normalizedMessage = (message || '').trim().toLowerCase();
  return (
    normalizedCode === 'sso_012' ||
    normalizedCode === 'signup_requires_invite' ||
    normalizedMessage.includes('requires invite') ||
    normalizedMessage.includes('requires a valid invite') ||
    normalizedMessage.includes('requires invite_id') ||
    normalizedMessage.includes('needs an invitation')
  );
}

/** JSON body from GET /api/v1/sso/{provider}/initiate */
export interface SsoInitiateResponse {
  authorization_url: string;
  state?: string;
}

/** User-facing hints keyed by guide §7 error codes */
const SSO_ERROR_HINTS: Record<string, string> = {
  SSO_001: 'Unknown provider. Use Google or Microsoft.',
  SSO_002: 'This return URL is not allowed. Ask your administrator to add it to allowed origins.',
  SSO_003: 'Sign-in was cancelled or consent was denied. You can try again.',
  SSO_004:
    'Security cookie missing. Use a fresh sign-in from the login or register page and ensure cookies are enabled (same site as the app).',
  SSO_005: 'Invitation not found. Check your invite link.',
  SSO_006: 'This invitation has expired or was already used. Request a new invitation if needed.',
  SSO_007: 'That account does not match the invited email. Sign in with the email from your invitation.',
  SSO_008: 'The sign-in provider failed temporarily. Try again in a moment.',
  SSO_009: 'Invalid session state. Start sign-in again from the login or register page.',
  SSO_010: 'Provider configuration error. Start sign-in again from the app.',
  SSO_012: 'This sign-up needs an invitation from your admin.',
};

/** Combines API message with optional friendly hint for known codes */
export function formatSsoCallbackErrorMessage(errorCode?: string | null, message?: string | null): string {
  const detail = (message || '').trim();
  // OAuth 2.0 uses error=access_denied when the user cancels at the provider
  if (errorCode === 'access_denied') {
    const hint = SSO_ERROR_HINTS.SSO_003;
    return detail ? `${hint} (${detail})` : hint;
  }
  const hint = errorCode && SSO_ERROR_HINTS[errorCode];
  if (hint && detail) return `${hint} ${detail}`;
  if (hint) return hint;
  if (detail) return errorCode ? `[${errorCode}] ${detail}` : detail;
  return 'Sign-in failed. Please try again.';
}

/**
 * When the backend redirects to the SPA with failure info in the URL (tokens are never in the URL).
 * Handles query, hash, or JSON-in-hash error payloads.
 */
export function parseSsoCallbackErrorsFromLocation(
  search: string,
  hashWithPound: string
): SsoCallbackError | null {
  const qs = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const hash = (hashWithPound || '').replace(/^#/, '');

  const readDomainStatus = (p: URLSearchParams): SsoDomainStatus | undefined => {
    const value = p.get('domain_status');
    return value === 'known' || value === 'unknown' || value === 'skipped' ? value : undefined;
  };

  const fromParams = (p: URLSearchParams): SsoCallbackError | null => {
    const errorCode = p.get('error_code') || p.get('error');
    const message = p.get('message') || p.get('error_description') || p.get('detail');
    if (errorCode) {
      return {
        errorCode: errorCode || undefined,
        message: formatSsoCallbackErrorMessage(errorCode, message),
        domainStatus: readDomainStatus(p),
        email: p.get('email')?.trim().toLowerCase() || undefined,
        accessRequestToken: p.get('access_request_token') || undefined,
      };
    }
    if (message) {
      return {
        message: formatSsoCallbackErrorMessage(undefined, message),
        email: p.get('email')?.trim().toLowerCase() || undefined,
        accessRequestToken: p.get('access_request_token') || undefined,
      };
    }
    return null;
  };

  const qErr = fromParams(qs);
  if (qErr) return qErr;

  if (hash) {
    const hp = new URLSearchParams(hash);
    const hErr = fromParams(hp);
    if (hErr) return hErr;

    const trimmed = hash.trimStart();
    if (trimmed.startsWith('{')) {
      try {
        const obj = JSON.parse(hash) as Record<string, unknown>;
        if (obj.error_code != null || obj.message != null) {
          const ec = obj.error_code;
          const em = obj.message;
          const domainStatus = obj.domain_status;
          return {
            errorCode: typeof ec === 'string' ? ec : undefined,
            message: formatSsoCallbackErrorMessage(
              typeof ec === 'string' ? ec : null,
              typeof em === 'string' ? em : null
            ),
            domainStatus:
              domainStatus === 'known' || domainStatus === 'unknown' || domainStatus === 'skipped'
                ? domainStatus
                : undefined,
            email: typeof obj.email === 'string' ? obj.email.trim().toLowerCase() : undefined,
            accessRequestToken:
              typeof obj.access_request_token === 'string' ? obj.access_request_token : undefined,
          };
        }
      } catch {
        /* ignore */
      }
    }
  }

  return null;
}

/** Result of reading SSO JSON embedded in the /sso-callback document by the backend */
export type SsoCallbackDocumentRead =
  | { status: 'success'; body: Record<string, unknown> }
  | { status: 'error'; errorCode?: string; message: string }
  | { status: 'missing' };

/** Window keys the backend may set when serving /sso-callback HTML before the app loads */
const SSO_WINDOW_PAYLOAD_KEYS = ['__PRISM7_SSO_CALLBACK__', '__SSO_CALLBACK_JSON__'] as const;

const SSO_JSON_SCRIPT_IDS = ['sso-callback-response', 'sso_callback_response', 'sso-response'] as const;

function interpretSsoDocumentJson(body: Record<string, unknown>): SsoCallbackDocumentRead {
  const access = body.access_token;
  const refresh = body.refresh_token;
  const hasTokens = typeof access === 'string' && typeof refresh === 'string' && access && refresh;

  const errCode = body.error_code;
  const errMsg = body.message ?? body.detail;

  if (hasTokens) {
    return { status: 'success', body };
  }

  if (typeof errCode === 'string' || typeof errMsg === 'string') {
    return {
      status: 'error',
      errorCode: typeof errCode === 'string' ? errCode : undefined,
      message: formatSsoCallbackErrorMessage(
        typeof errCode === 'string' ? errCode : null,
        typeof errMsg === 'string' ? errMsg : null
      ),
    };
  }

  return { status: 'missing' };
}

/**
 * Reads SSO success/error JSON placed in this document by the backend when loading `/sso-callback`
 * (no extra API call). Supports: window global, JSON `script` tag (`sso-callback-response` and similar ids), `data-sso-callback` script, or `application/json` document.
 */
export function readSsoCallbackJsonFromDocument(): SsoCallbackDocumentRead {
  for (const key of SSO_WINDOW_PAYLOAD_KEYS) {
    const w = window as unknown as Record<string, unknown>;
    const raw = w[key];
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
      const interpreted = interpretSsoDocumentJson(raw as Record<string, unknown>);
      if (interpreted.status !== 'missing') return interpreted;
    }
  }

  for (const id of SSO_JSON_SCRIPT_IDS) {
    const el = document.getElementById(id);
    const text = el?.textContent?.trim();
    if (!text) continue;
    try {
      const body = JSON.parse(text) as Record<string, unknown>;
      const interpreted = interpretSsoDocumentJson(body);
      if (interpreted.status !== 'missing') return interpreted;
    } catch {
      return { status: 'error', message: 'Invalid SSO data in page (could not parse JSON).' };
    }
  }

  const dataAttrScript = document.querySelector('script[type="application/json"][data-sso-callback]');
  const attrText = dataAttrScript?.textContent?.trim();
  if (attrText) {
    try {
      const body = JSON.parse(attrText) as Record<string, unknown>;
      const interpreted = interpretSsoDocumentJson(body);
      if (interpreted.status !== 'missing') return interpreted;
    } catch {
      return { status: 'error', message: 'Invalid SSO data in page (could not parse JSON).' };
    }
  }

  const ct = document.contentType || '';
  if (ct.includes('application/json')) {
    const raw =
      document.body?.textContent?.trim() || document.documentElement?.textContent?.trim() || '';
    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        const body = JSON.parse(raw) as Record<string, unknown>;
        return interpretSsoDocumentJson(body);
      } catch {
        return { status: 'error', message: 'Invalid SSO data: could not parse JSON document.' };
      }
    }
  }

  return { status: 'missing' };
}

/** Absolute frontend URL registered with the backend as redirect_uri (backend then serves this page with JSON in the document). */
export function getSsoCallbackRedirectUri(): string {
  const baseUrl = import.meta.env.BASE_URL || '/';
  const trimmed = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathPrefix = trimmed === '/' ? '' : trimmed;
  return `${window.location.origin}${pathPrefix}/sso-callback`;
}

/**
 * Calls initiate API and returns the provider authorization URL (caller redirects the window).
 * invite_id is omitted for login; include for invite-based registration SSO.
 */
export async function fetchSsoInitiateUrl(
  provider: SsoProvider,
  options: { redirectUri: string; inviteId?: string }
): Promise<SsoInitiateResponse> {
  const search = new URLSearchParams();
  search.set('redirect_uri', options.redirectUri);
  if (options.inviteId) {
    search.set('invite_id', options.inviteId);
  }

  const path = API_ENDPOINTS.SSO.INITIATE(provider);
  const url = `${API_CONFIG.API_BASE_URL}${path}?${search.toString()}`;
  // credentials: 'include' is required (same as axios withCredentials: true) so sso_csrf Set-Cookie is stored
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const rawText = await res.text();

  if (!res.ok) {
    let message = `Could not start ${provider} sign-in (${res.status})`;
    try {
      const body = JSON.parse(rawText) as SsoErrorBody;
      const code = body?.error_code;
      const detail = body?.message || body?.detail;
      message = formatSsoCallbackErrorMessage(code, detail);
    } catch {
      if (rawText) message = rawText.slice(0, 200);
    }
    throw new Error(message);
  }

  return JSON.parse(rawText) as SsoInitiateResponse;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Maps backend JSON success body (e.g. GET /sso/session) into LoginResponse; falls back to JWT claims where needed. */
export function buildLoginResponseFromSsoJsonBody(body: Record<string, unknown>): LoginResponse {
  const accessToken = String(body.access_token ?? '');
  const refreshToken = String(body.refresh_token ?? '');
  const payload = decodeJwtPayload(accessToken);
  const role =
    (typeof body.role === 'string' ? body.role : null) ||
    (typeof payload?.role === 'string' ? payload.role : null) ||
    USER_ROLES.USER;

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: typeof body.token_type === 'string' ? body.token_type : 'Bearer',
    expires_in: typeof body.expires_in === 'number' ? body.expires_in : Number(body.expires_in) || 3600,
    user_id:
      (typeof body.user_id === 'string' ? body.user_id : '') ||
      (typeof payload?.user_id === 'string' ? payload.user_id : '') ||
      (typeof payload?.sub === 'string' ? payload.sub : '') ||
      '',
    email_id:
      (typeof body.email_id === 'string' ? body.email_id : '') ||
      (typeof payload?.email_id === 'string' ? payload.email_id : '') ||
      (typeof payload?.email === 'string' ? payload.email : '') ||
      '',
    name:
      (typeof body.name === 'string' ? body.name : '') ||
      (typeof payload?.name === 'string' ? payload.name : '') ||
      '',
    full_name:
      (typeof body.full_name === 'string'
        ? body.full_name
        : typeof body.fullName === 'string'
          ? body.fullName
          : undefined) ||
      (typeof payload?.full_name === 'string'
        ? payload.full_name
        : typeof payload?.fullName === 'string'
          ? payload.fullName
          : undefined),
    role,
    company_id:
      (typeof body.company_id === 'string' ? body.company_id : '') ||
      (typeof payload?.company_id === 'string' ? payload.company_id : '') ||
      '',
    login_type: 'sso',
  };
}
